import React, { ReactNode, useMemo } from 'react';
import { Button } from '@cp/ds/src/components/button';
import { Box } from '@cp/ds/src/components/box';
import { Stack } from '@cp/ds/src/components/stack';
import { useIsMobile } from '@cp/ds/src/hooks/useIsMobile';
import { AdvantageList, AdvantageTokens } from '@cp/entities/advantage';
import { ClientLastSeenText, days190strategy } from '@cp/entities/clientOnlineStatus';
import { Schema } from '@cp/ds/src/components/schema';
import { useIsLoggedIn } from '@cp/entities/currentUser';
import { useGetPromocodes } from '@cp/entities/promocode';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Container } from '@cp/ds/src/components/container';
import useMediaQuery from '@mui/material/useMediaQuery';
import { AppTheme } from '@cp/ds';
import { ItemResumeCard, ResumeTabs } from 'src/features/resume';
import { Achievement, UserMainData } from 'src/entities/resume';
import { useCanInvite, useCatalogItemAdvantages, useInviteSpecialist, useProfilePageSchema } from '../../model';
import type { ResumeProps } from './resume';
import { ResumeFavorite } from './favorite';

export const ResumeV2: React.FC<ResumeProps> = ({ data, ...props }) => {
  const isMobile = useIsMobile();
  const loggedIn = useIsLoggedIn();
  const promo = useGetPromocodes();
  const isAvailable = useCanInvite(data);
  const jsonSchema = useProfilePageSchema(data);
  const inviteSpecialist = useInviteSpecialist(data);
  const isSmallScreen = useMediaQuery((theme: AppTheme) => theme.breakpoints.down('lg'));
  const clientWithPromo = !loggedIn || promo.length > 0;

  const SectionContainer = isMobile ? Container : Box;

  const onlineStatus = useMemo(() => {
    return props.showOnlineStatus && data?.clientProfile && <ClientLastSeenText profile={data?.clientProfile} strategy={days190strategy} />;
  }, [data?.clientProfile, props.showOnlineStatus]);

  const advantageList = useCatalogItemAdvantages(data);
  const advantages: ReactNode = useMemo(() => {
    const advantageProps = { bordered: true };
    return (
      <AdvantageList
        advantageProps={advantageProps}
        advantages={advantageList}
        sx={{ gap: 1, display: 'flex', flexWrap: 'wrap', [AdvantageTokens.colorBackground]: 'white' }}
      />
    );
  }, [advantageList]);

  if (props.isLoading) {
    return (
      <SectionContainer>
        <ContentSkeleton blocks={2} spacing={1} />
      </SectionContainer>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Stack sx={{ width: 1, pb: '20px' }}>
      <ItemResumeCard
        achievements={
          <>
            {data.clientProfile?.trainingCompletedAt && <Achievement type="school" />}
            {data.clientProfile?.isDocumentsChecked && <Achievement type="verified" />}
            {data.clientProfile?.isOnline && <Achievement type="online" />}
          </>
        }
        data={data}
        favoriteButton={
          isSmallScreen && <ResumeFavorite clientId={data.clientProfile?.id || 0} itemSx={{ width: '36px', height: '36px' }} />
        }
        onlineStatus={onlineStatus}
      />

      {isMobile && <UserMainData data={data} isMobile onlineStatus={onlineStatus} />}

      {isSmallScreen && (
        <Box sx={{ maxWidth: '480px', width: '100%', px: '12px', mt: { xs: 0, md: '20px' } }}>
          <Button color="gray" disabled={!isAvailable} onClick={inviteSpecialist} size="normal" sx={{ width: '100%' }} variant="primary">
            Связаться {clientWithPromo ? 'бесплатно' : ''}
          </Button>
        </Box>
      )}

      <ResumeTabs advantages={advantages} data={data} />

      <Schema schema={jsonSchema} />
    </Stack>
  );
};
ResumeV2.displayName = 'widget/ResumeV2';
